/**
 * useScope Hook
 *
 * Centralized scope state management for filtering projects across views.
 * Supports: All Projects, Last Import, Collections, and Persona Presets.
 */

import { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { useAuth } from '../components/auth';
import { getLastImportSession, listImportSessions } from '../services/projectsApi';
import { listCollections } from '../services/collectionsApi';

// ============================================
// Scope Types
// ============================================

export const SCOPE_TYPES = {
  ALL: 'all',
  LAST_IMPORT: 'last_import',
  IMPORT_SESSION: 'import_session',
  COLLECTION: 'collection',
};

// ============================================
// Persona Presets (client-side)
// ============================================

export const PERSONA_PRESETS = {
  open_source_contributor: {
    key: 'open_source_contributor',
    name: 'Open Source Contributor',
    description: 'Focus on public repositories and community metrics',
    filters: { is_private: false },
    icon: 'Globe',
  },
  enterprise_portfolio: {
    key: 'enterprise_portfolio',
    name: 'Enterprise Portfolio',
    description: 'Focus on private repositories and business value',
    filters: { is_private: true },
    icon: 'Building2',
  },
  full_stack: {
    key: 'full_stack',
    name: 'Full Stack',
    description: 'Balanced view of all repositories',
    filters: {},
    icon: 'Layers',
  },
  startup_founder: {
    key: 'startup_founder',
    name: 'Startup Founder',
    description: 'Focus on MVPs, speed, and market validation',
    filters: {},
    icon: 'Rocket',
  },
  architect: {
    key: 'architect',
    name: 'Architect',
    description: 'Focus on system design and integration patterns',
    filters: {},
    icon: 'Network',
  },
};

// ============================================
// Local Storage Key
// ============================================

const SCOPE_STORAGE_KEY = 'portfolistic_scope';

// ============================================
// Context
// ============================================

const ScopeContext = createContext(null);

// ============================================
// Provider Component
// ============================================

export function ScopeProvider({ children }) {
  const { user } = useAuth();

  // Core scope state
  const [scopeType, setScopeType] = useState(SCOPE_TYPES.ALL);
  const [scopeId, setScopeId] = useState(null); // session_id or collection_id
  const [persona, setPersona] = useState(null);

  // Related data
  const [collections, setCollections] = useState([]);
  const [importSessions, setImportSessions] = useState([]);
  const [lastImport, setLastImport] = useState(null);
  const [currentScope, setCurrentScope] = useState(null);

  // Loading state
  const [loading, setLoading] = useState(false);

  // Load scope from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SCOPE_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.scopeType) setScopeType(parsed.scopeType);
        if (parsed.scopeId) setScopeId(parsed.scopeId);
        if (parsed.persona) setPersona(parsed.persona);
      }
    } catch (e) {
      console.warn('Failed to load scope from localStorage:', e);
    }
  }, []);

  // Save scope to localStorage when changed
  useEffect(() => {
    try {
      localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify({
        scopeType,
        scopeId,
        persona,
      }));
    } catch (e) {
      console.warn('Failed to save scope to localStorage:', e);
    }
  }, [scopeType, scopeId, persona]);

  // Load collections and import sessions when user changes
  useEffect(() => {
    if (!user) {
      setCollections([]);
      setImportSessions([]);
      setLastImport(null);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const [collectionsData, sessionsData, lastImportData] = await Promise.allSettled([
          listCollections(user),
          listImportSessions(user, { limit: 10 }),
          getLastImportSession(user),
        ]);

        if (collectionsData.status === 'fulfilled') {
          setCollections(collectionsData.value || []);
        }

        if (sessionsData.status === 'fulfilled') {
          setImportSessions(sessionsData.value || []);
        }

        if (lastImportData.status === 'fulfilled') {
          setLastImport(lastImportData.value?.session || null);
        }
      } catch (error) {
        console.error('Error loading scope data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Update currentScope when scope selection changes
  useEffect(() => {
    if (scopeType === SCOPE_TYPES.ALL) {
      setCurrentScope({ type: SCOPE_TYPES.ALL, name: 'All Projects', projects: null });
    } else if (scopeType === SCOPE_TYPES.LAST_IMPORT && lastImport) {
      setCurrentScope({
        type: SCOPE_TYPES.LAST_IMPORT,
        name: 'Last Import',
        sessionId: lastImport.id,
        projects: lastImport.projects || [],
        projectCount: lastImport.project_count,
      });
    } else if (scopeType === SCOPE_TYPES.IMPORT_SESSION && scopeId) {
      const session = importSessions.find(s => s.id === scopeId);
      if (session) {
        setCurrentScope({
          type: SCOPE_TYPES.IMPORT_SESSION,
          name: `Import: ${new Date(session.created_at).toLocaleDateString()}`,
          sessionId: session.id,
          projects: session.projects || [],
          projectCount: session.project_count,
        });
      }
    } else if (scopeType === SCOPE_TYPES.COLLECTION && scopeId) {
      const collection = collections.find(c => c.id === scopeId);
      if (collection) {
        setCurrentScope({
          type: SCOPE_TYPES.COLLECTION,
          name: collection.name,
          collectionId: collection.id,
          projects: collection.projects || [],
          projectCount: collection.project_count,
          color: collection.color,
          persona: collection.persona_type,
        });
      }
    } else {
      setCurrentScope({ type: SCOPE_TYPES.ALL, name: 'All Projects', projects: null });
    }
  }, [scopeType, scopeId, lastImport, importSessions, collections]);

  // Scope selection functions
  const selectAllProjects = useCallback(() => {
    setScopeType(SCOPE_TYPES.ALL);
    setScopeId(null);
  }, []);

  const selectLastImport = useCallback(() => {
    if (lastImport) {
      setScopeType(SCOPE_TYPES.LAST_IMPORT);
      setScopeId(lastImport.id);
    }
  }, [lastImport]);

  const selectImportSession = useCallback((sessionId) => {
    setScopeType(SCOPE_TYPES.IMPORT_SESSION);
    setScopeId(sessionId);
  }, []);

  const selectCollection = useCallback((collectionId) => {
    setScopeType(SCOPE_TYPES.COLLECTION);
    setScopeId(collectionId);
  }, []);

  const selectPersona = useCallback((personaKey) => {
    setPersona(personaKey);
  }, []);

  const clearPersona = useCallback(() => {
    setPersona(null);
  }, []);

  // Refresh data
  const refresh = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const [collectionsData, sessionsData, lastImportData] = await Promise.all([
        listCollections(user).catch(() => []),
        listImportSessions(user, { limit: 10 }).catch(() => []),
        getLastImportSession(user).catch(() => ({ session: null })),
      ]);

      setCollections(collectionsData || []);
      setImportSessions(sessionsData || []);
      setLastImport(lastImportData?.session || null);
    } catch (error) {
      console.error('Error refreshing scope data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Compute active filters based on persona
  const activeFilters = useMemo(() => {
    if (!persona) return {};
    const preset = PERSONA_PRESETS[persona];
    return preset?.filters || {};
  }, [persona]);

  // Filter projects function
  const filterProjects = useCallback((projects) => {
    if (!projects) return projects;

    let filtered = [...projects];

    // Apply scope filter (if scope has specific projects)
    if (currentScope?.projects) {
      const scopeProjectIds = new Set(currentScope.projects.map(p => p.id));
      filtered = filtered.filter(p => scopeProjectIds.has(p.id));
    }

    // Apply persona filters
    if (persona && activeFilters) {
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (key === 'is_private' && value !== undefined) {
          filtered = filtered.filter(p => p.isPrivate === value || p.is_private === value);
        }
      });
    }

    return filtered;
  }, [currentScope, persona, activeFilters]);

  const value = {
    // State
    scopeType,
    scopeId,
    persona,
    currentScope,
    collections,
    importSessions,
    lastImport,
    loading,
    activeFilters,

    // Actions
    selectAllProjects,
    selectLastImport,
    selectImportSession,
    selectCollection,
    selectPersona,
    clearPersona,
    refresh,
    filterProjects,

    // Constants
    SCOPE_TYPES,
    PERSONA_PRESETS,
  };

  return (
    <ScopeContext.Provider value={value}>
      {children}
    </ScopeContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useScope() {
  const context = useContext(ScopeContext);
  if (!context) {
    throw new Error('useScope must be used within a ScopeProvider');
  }
  return context;
}

export default useScope;
